import _ from 'lodash';
import React, { useContext, useEffect, useRef } from 'react';
import { Form, FormGroup } from 'react-bootstrap';
import { Col, Row } from 'reactstrap';
import CountrySelectService from '../../../../../components/CSC/CountrySelect/CountrySelectService';
import { isEmploymentStatusEmployed, normalizeBooleanLikeValue } from '../../../../../components/InputField/helper';
import InlineHelpTooltip from '../../../../../components/InputField/InlineHelpTooltip';
import PreviousValue from '../../../../../components/InputField/PreviousValue';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import { createWrapperFieldRenderer } from '../../helpers/createWrapperFieldRenderer';
import { validateRequiredFields } from './helper';
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from '../../../../../components/InputField/testId';
import { VARIANT, variantClass } from '../../../../../util/variant';
import DocumentFirstStepHead from '../../BusinessV2/components/DocumentFirstStepHead';
import '../../BusinessV2/documentsVersion2.css';

const DECLARATIONS_TEST_ID = "individual-plus-declarations";

const DeclarationsForm = ({
    hideButtons,
    asLabel,
    data,
    handleChangeObject,
    handleUpdate,
    hasError,
    setHasError,
    setErrorMessage = () => { },
    loading,
    setStep,
    cyclosFields,
    // Doc-first GMA (formVersion >= 2 broker) adds Source of Funds + the V2 field theme; legacy
    // (existing onboarding + older GMA broker) renders exactly as before.
    isDocumentFirst = false,
    sysConfigForCardMandatoryForIPlus,
    backStep = 1,
    nextStep = 3,
    pageNum,
    stepCount = 6,
    stepLabel = 'Declarations',
}) => {
    const { t } = useTranslation();

    const { showError } = useContext(DialogContext);

    const questions1 = ['hasBankAccount', 'hasCriminalRecord'];
    const questions3 = ['isPoliticallyExposed'];
    const questions2 = ['interestedInCrypto'];

    const renderWrapperField = createWrapperFieldRenderer({
        fieldMap: cyclosFields,
        dataObj: data,
        disabled: asLabel,
        showError: hasError,
        defaultOnChange: handleChangeObject,
        variant: isDocumentFirst ? VARIANT.V2 : undefined,
    });

    const handleSubmit = () => {
        if (data?.annualSalary > 2147483647) return showError("Currency should be less than 2147483647");

        const requiredFields = [
            { field: 'hasBankAccount', fieldType: 'checkbox' },
            { field: 'hasCriminalRecord', fieldType: 'checkbox' },
            ...(data?.hasCriminalRecord ? [{ field: 'criminalRecordDetail', fieldType: null }] : [null]),
            { field: 'isPoliticallyExposed', fieldType: 'checkbox' },
            { field: 'employmentStatus', fieldType: null },
            { field: 'annualSalary', fieldType: null },
            ...(isEmploymentStatusEmployed(data?.employmentStatus) ? [{ field: 'employmentIndustry', fieldType: null }] : []),
            { field: 'purposeOfAccount', fieldType: null },
            ...(isDocumentFirst ? [{ field: 'sourceOfFund', fieldType: null }] : []),
            { field: 'sourcesOfWealth', fieldType: null },
            { field: 'depositLocations', fieldType: null },
            { field: 'paymentLocations', fieldType: null },
            { field: 'interestedInCrypto', fieldType: 'checkbox' },
        ].filter(Boolean);

        if (!(CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[CountrySelectService?.COUNTRY_MASTER_KEYS?.VISA_CARD] == "No"
            || CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[CountrySelectService.COUNTRY_MASTER_KEYS.SANCTION_COUNTRY] == "Yes"
            || sysConfigForCardMandatoryForIPlus)) {
            requiredFields.push({ field: 'cardAvail', fieldType: 'checkbox' });
        }

        const errors = validateRequiredFields(requiredFields, data, cyclosFields);

        if (errors.length > 0) {
            setHasError(true);
            setErrorMessage(errors.join(' '));
            showError(errors[0]);
            return;
        }

        setHasError(false);
        const payload = data?.hasCriminalRecord ? data : _.omit(data, ['criminalRecordDetail']);
        return handleUpdate({ nextStep, payload });
    };

    // Doc-first (GMA broker, formVersion >= 2) renders the V2 card/group shell. The legacy
    // individual flow — existing onboarding and older GMA brokers — must keep its original
    // `row pt-3` + display:contents layout, so every V2 wrapper below is gated on isDocumentFirst.
    const sectionWrapProps = isDocumentFirst
        ? { className: 'col-12' }
        : { style: { display: 'contents' } };

    const declarationSections = (
        <>
            <div {...sectionWrapProps} data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "background-section")}>
                {questions1.map((field) => (
                    renderWrapperField(field)
                ))}

                {/* Legacy only: the served uiClassName makes this textarea half-width, so the next
                    question wraps alongside it. Force full width. Doc-first keeps its
                    config-driven width (INDIVIDUAL_V2_0 owns the V2 presentation). */}
                {renderWrapperField('criminalRecordDetail', isDocumentFirst ? undefined : { className: 'col-12' })}

                {questions3.map((field) => (
                    renderWrapperField(field)
                ))}
            </div>

            <Row className='mb-2' data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "employment-section")}>
                <Col className='col-12 col-md-6'>
                    {renderWrapperField('employmentStatus', {
                        onChange: (nextData) => handleChangeObject({
                            ...nextData,
                            employmentIndustry: null,
                        }),
                    })}
                </Col>
                <Col className='col-12 col-md-6'>
                    {renderWrapperField('annualSalary')}
                </Col>
            </Row>

            {isEmploymentStatusEmployed(data?.employmentStatus) ? <Row className='mb-2' data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "employment-industry-section")}>
                <Col className='col-12'>
                    {renderWrapperField('employmentIndustry')}
                </Col>
            </Row> : null}

            <div style={{ display: "contents" }} data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "account-purpose-section")}>
                {renderWrapperField('purposeOfAccount')}

                {isDocumentFirst && renderWrapperField('sourceOfFund')}

                {renderWrapperField('sourcesOfWealth')}

                {renderWrapperField('depositLocations')}

                {renderWrapperField('paymentLocations')}
            </div>

            <div {...sectionWrapProps} data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "crypto-section")}>
                {questions2.map((field) => (
                    renderWrapperField(field)
                ))}
            </div>

            <div {...sectionWrapProps} data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "card-availability-section")}>
                {(CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[CountrySelectService?.COUNTRY_MASTER_KEYS?.VISA_CARD] == "No"
                    || CountrySelectService?.cyclosCountryMasterMap?.[data?.address?.country]?.[CountrySelectService.COUNTRY_MASTER_KEYS.SANCTION_COUNTRY] == "Yes"
                    || sysConfigForCardMandatoryForIPlus) ? null :
                    renderWrapperField('cardAvail', { requiredOverride: true })
                }
            </div>
        </>
    );

    return (
        <div className={isDocumentFirst ? `docfirst ${variantClass(VARIANT.V2)}`.trim() : 'row pt-3'} data-testid={DECLARATIONS_TEST_ID}>
            {!hideButtons && isDocumentFirst && (
                <DocumentFirstStepHead
                    stepIndex={pageNum}
                    stepCount={stepCount}
                    stepLabel={stepLabel}
                    title={t(stepLabel)}
                    intro={t('Tell us about your background and how you plan to use the account.')}
                    dataTestId={buildAutomationChildTestId(DECLARATIONS_TEST_ID, 'head')}
                />
            )}
            {isDocumentFirst ? (
                <div className="docfirst__card">
                    <div className="docfirst__group">
                        <div className='row'>
                            {declarationSections}
                        </div>
                    </div>
                </div>
            ) : declarationSections}
            {!hideButtons && (
                <div className='mt-4' data-testid={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "footer")}>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => { setStep(backStep) }}
                        step={2}
                        showPageNum={false}
                        pageNum={2}
                        isDisabled={asLabel}
                        asLabel={false}
                        loading={loading}
                        testId={buildAutomationChildTestId(DECLARATIONS_TEST_ID, "navigation")}
                    />
                </div>
            )}
        </div>
    );
};

export const QuestionRadioGroup = ({ id, desc, tooltip, isDisabled, field, question, data, handleChange, hasError, style, defaultValue, required, error, prevValue, preventDefault = false }) => {
    const { t } = useTranslation()
    const fieldTestId = resolveAutomationTestId(id, field);
    const yesTestId = buildAutomationValueTestId(fieldTestId, "yes");
    const noTestId = buildAutomationValueTestId(fieldTestId, "no");
    const rawFieldValue = _.get(data, field);
    const normalizedFieldValue = normalizeBooleanLikeValue(rawFieldValue);
    const normalizedDefaultValue = normalizeBooleanLikeValue(defaultValue);
    const normalizedPrevValue = normalizeBooleanLikeValue(prevValue?.value);
    const handleChangeRef = useRef(handleChange);
    const autoSyncSignatureRef = useRef(null);

    useEffect(() => {
        handleChangeRef.current = handleChange;
    }, [handleChange]);

    useEffect(() => {
        if (preventDefault) {
            autoSyncSignatureRef.current = null;
            return;
        }

        let nextAutoValue;
        if (rawFieldValue == null && typeof normalizedDefaultValue === 'boolean') {
            nextAutoValue = normalizedDefaultValue;
        } else if (typeof normalizedFieldValue === 'boolean' && rawFieldValue !== normalizedFieldValue) {
            nextAutoValue = normalizedFieldValue;
        } else {
            autoSyncSignatureRef.current = null;
            return;
        }

        const nextSignature = `${field}:${String(rawFieldValue)}:${String(nextAutoValue)}`;
        if (autoSyncSignatureRef.current === nextSignature) {
            return;
        }

        autoSyncSignatureRef.current = nextSignature;
        handleChangeRef.current?.(field, nextAutoValue);
    }, [field, normalizedDefaultValue, normalizedFieldValue, preventDefault, rawFieldValue]);

    const disabledStyle = isDisabled
        ? { color: "#999", cursor: "not-allowed" }
        : {};

    return (
        <div className='fv__selection-field fv__selection-field--radio mt-1 mb-2' data-testid={fieldTestId}>
            <FormGroup className='px-1' >
                <Form.Label
                    className=''
                    style={{ minWidth: "120px", padding: "0px 0px 0px 0px", fontSize: 16, fontWeight: "500", ...style }}
                    data-testid={buildAutomationChildTestId(fieldTestId, "label")}
                >
                    {t(question)}
                    {required && <font color="red">*</font>}
                    {tooltip ? (
                        <InlineHelpTooltip
                            className="ms-2"
                            content={t(tooltip)}
                            ariaLabel={`${t(question)} help`}
                            testId={buildAutomationChildTestId(fieldTestId, "help")}
                        />
                    ) : null}
                </Form.Label>
                <div className='d-flex ps-0' data-testid={buildAutomationChildTestId(fieldTestId, "options")}>
                    <Form.Check
                        id={yesTestId}
                        data-testid={yesTestId}
                        disabled={isDisabled}
                        className={`me-4 custom-radio ${prevValue?.has ? "is-changed" : isDisabled ? "is-disabled" : ""}`}
                        type="radio"
                        label={t("Yes")}
                        name={field}
                        value="true"
                        checked={normalizedFieldValue === true}
                        onChange={() => handleChange(field, true)}
                        style={disabledStyle}
                    />
                    <Form.Check
                        id={noTestId}
                        data-testid={noTestId}
                        disabled={isDisabled}
                        type="radio"
                        className={`custom-radio ${prevValue?.has ? "is-changed" : isDisabled ? "is-disabled" : ""}`}
                        label={t("No")}
                        name={field}
                        value="false"
                        checked={normalizedFieldValue === false}
                        onChange={() => handleChange(field, false)}
                        style={disabledStyle}
                    />
                </div>
                {desc ? <em className="ps-0 mt-1 ms-0 w-100  left-0 inputfield__desc d-block " data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
                    {desc ? t(desc) : ""}
                </em> : null}
                <PreviousValue
                    show={prevValue?.has}
                    value={typeof normalizedPrevValue === 'boolean' ? normalizedPrevValue ? "Yes" : "No" : null}
                    testId={buildAutomationChildTestId(fieldTestId, "previous")}
                />
                {error && hasError && <div className="ps-0 mt-1 ms-0 w-100 error__message  left-0" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error) || ""}</div>}
            </FormGroup>
        </div>
    );
};

export default DeclarationsForm;
